/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : ForgotPassword.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */

import { Avatar, Box, IconButton, Link, Typography } from "@mui/material";
import React, { useState } from "react";
import PropTypes from "prop-types";
import { AuthTextField } from "..";
import appMeta from "../../assets/app-meta/appMeta";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CustomButtonV2 from "../customComponentsV2/common/button/CustomButtonV2";

function ForgotPassword({
  throttledHandleForgotPassword,
  handleAuthViewChange,
}) {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");

  const handleLoginOnClick = () => {
    handleAuthViewChange(appMeta.authView.LOGIN);
  };

  const handleSendOtpOnClick = () => {
    const emailRegex = /^[^@\s]+@[^@\s.]+\.[^@\s.]+$/;

    if (!emailRegex.test(email)) {
      setEmailError("Invalid email address");
      return;
    }

    setEmailError("");

    throttledHandleForgotPassword({ email });
  };

  return (
    <Box sx={styles.mainContainer}>
      <Avatar variant="rounded" sx={styles.avatar}>
        <img height={"100%"} src="deloitteIconBlackLarge.png" alt="Deloitte" />
      </Avatar>

      <Typography style={styles.fpTxt} component="h1" variant="body1">
        Forgot Password
      </Typography>

      <Box sx={styles.fldsContainer}>
        <AuthTextField
          label={"Email"}
          placeHolder={"Email"}
          type={"email"}
          fldType={appMeta.textFieldTypes.EMAIL}
          variant={"standard"}
          value={email}
          setValue={setEmail}
          valueError={emailError}
          setValueError={setEmailError}
          isAutoFocused={true}
          isRequired={true}
          onKeyDown={(event) => {
            if (event.key === "Enter") handleSendOtpOnClick();
          }}
          txtFldStyle={styles.authTextField}
        />
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <CustomButtonV2
            type="submit"
            btnName="Send Code"
            handleOnClick={handleSendOtpOnClick}
          />
        </Box>
        <Box
          onClick={(e) => {
            e.preventDefault();
            handleLoginOnClick();
          }}
          sx={{ mt: 2 }}
        >
          <IconButton sx={styles.iconBtn} aria-label="back to login">
            <ArrowBackIcon />
          </IconButton>
          <Link style={styles.linkStyle} href="#" variant="body2">
            Back to Login
          </Link>
        </Box>
      </Box>
    </Box>
  );
}

ForgotPassword.propTypes = {
  throttledHandleForgotPassword: PropTypes.func,
  handleAuthViewChange: PropTypes.func,
};

export default ForgotPassword;

const styles = {
  mainContainer: {
    width: "100%",
    paddingX: 5,
    paddingY: 4,
    borderRadius: 1,
    backgroundColor: "white",
    border: { sm: "1px solid lightgrey" },
    boxShadow: { sm: "0px 2px 4px rgba(0, 0, 0, 0.1)" },
    boxSizing: "border-box",
  },
  fldsContainer: {
    width: "100%",
  },
  avatar: {
    mb: 3,
    bgcolor: "white",
    width: "100%",
    height: 24,
  },
  linkStyle: {
    color: "#1976d2",
  },
  fpTxt: {
    fontWeight: 600,
  },
  authTextField: {
    mt: 1,
  },
  iconBtn: {
    color: "#1976d2",
    "& svg": {
      fontSize: "20px",
    },
  },
};
